import { useInventorySubtotalNumbers } from '@hooks/subtotal-numbers'
import { SubtotalFeeLines } from '@modules/booking/views/subtotal-fee-lines'
import { SubtotalLineItem } from '@modules/booking/views/subtotal-line-item'
import { formatPercent, formatPrice } from '@utils/strings'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { InventoryItem, Venue } from 'types/model'

type InventorySubototalDisplayProps = {
  quantity?: number | null | undefined
  inventory: InventoryItem
  venue: Venue
}

export function InventorySubtotalDisplay({
  inventory,
  quantity,
  venue,
}: InventorySubototalDisplayProps) {
  const { t } = useTranslation()
  const {
    deposit,
    processing,
    dueOnArrival,
    processingPercent,
    grandTotal,
    tax,
    taxPercent,
    tip,
    tipPercent,
  } = useInventorySubtotalNumbers(inventory, venue)

  const isAdvanceDeposit = inventory.paymentType === 'ADVANCE_DEPOSIT'
  const noPaymentToday = ['NONE', 'VENUE'].includes(inventory.paymentType)
  const hideZeroMinimumDepositPrice =
    isAdvanceDeposit && inventory.price <= 0 && (deposit ?? 0) > 0

  const price = useCallback(
    (val: number) =>
      formatPrice(val, {
        currency: venue.currencyCode,
        languageCode: venue.languageCode,
      }),
    [venue.currencyCode, venue.languageCode]
  )

  const customFees = useMemo(() => {
    if (inventory.fee?.enable)
      return inventory.fee?.fees?.map((fee) => ({
        ...fee,
        value:
          fee.value ||
          (fee.type === 'PERCENT' ? fee.amount * inventory.price : fee.amount),
      }))
    else return []
  }, [inventory.fee?.enable, inventory.fee?.fees, inventory.price])

  return (
    <div className='w-full p-3 space-y-2 border rounded shadow-sm text-body bg-card border-standard-xlight'>
      {/* Item Price and Quantity */}
      <div className='flex flex-col space-y-1'>
        <div className='flex items-start justify-between w-full'>
          <div className='max-w-[75%] flex flex-col items-start space-y-1'>
            <span className='font-bold '>{inventory.name}</span>
          </div>
          <span className='pt-0.5 text-right truncate text-ellipsis'>
            {!hideZeroMinimumDepositPrice
              ? inventory.price
                ? price(inventory.price)
                : t('buyer.inventory.free')
              : null}{' '}
            {!hideZeroMinimumDepositPrice && inventory.type === 'TICKET'
              ? `(x${quantity})`
              : null}
          </span>
        </div>
      </div>
      {!isAdvanceDeposit && inventory.fee.enable && (
        <>
          <SubtotalFeeLines
            customFees={customFees ?? []}
            userPaysProcessing={!noPaymentToday && inventory.userPaysProcessing}
            venue={venue}
            processingValue={processing}
            processingPercent={processingPercent}
            taxValue={tax}
            taxPercent={taxPercent}
            tipValue={tip}
            tipPercent={tipPercent}
          />
          <SubtotalLineItem
            name={t('buyer.checkout.total')}
            total={grandTotal}
            currency={venue.currencyCode}
            languageCode={venue.languageCode}
            nameClassName='!font-bold !text-base sm:!text-md'
            totalClassName='!font-bold !text-base sm:!text-md'
          />
          <hr className='w-full border-card' />
        </>
      )}
      {isAdvanceDeposit && (
        <>
          <SubtotalLineItem
            name={t('buyer.inventory.deposit')}
            total={deposit}
            currency={venue.currencyCode}
            languageCode={venue.languageCode}
          />
          {inventory.userPaysProcessing && (
            <SubtotalLineItem
              name={`${t('buyer.inventory.processing_fee')} ${
                processingPercent ? `(${formatPercent(processingPercent)})` : ''
              }`}
              total={processing}
              currency={venue.currencyCode}
              languageCode={venue.languageCode}
            />
          )}
        </>
      )}
      <SubtotalLineItem
        name={t('buyer.inventory.due_today')}
        total={noPaymentToday ? 0 : grandTotal}
        currency={venue.currencyCode}
        languageCode={venue.languageCode}
        nameClassName='!font-bold !text-base sm:!text-md'
        totalClassName='!font-bold !text-primary-500 !text-base sm:!text-md'
      />
      {!!dueOnArrival && (
        <>
          <hr className='w-full border-card' />
          <SubtotalLineItem
            name={t('buyer.inventory.estimated_due_on_arrival')}
            total={dueOnArrival}
            currency={venue.currencyCode}
            languageCode={venue.languageCode}
            nameClassName='text-sm truncate text-ellipsis sm:!text-base !font-normal'
            totalClassName='text-right text-ellipsis'
          />
          {isAdvanceDeposit && inventory.fee.enable && (
            <SubtotalFeeLines
              customFees={customFees ?? []}
              userPaysProcessing={inventory.userPaysProcessing}
              venue={venue}
              taxValue={tax}
              taxPercent={taxPercent}
              tipValue={tip}
              tipPercent={tipPercent}
            />
          )}
        </>
      )}
    </div>
  )
}
